import { useI18n } from "../use-i18n";
import { cn } from "../utils";
import { NcButton } from "./nc-button";
import { NcLoadingIndicator } from "./nc-loading-indicator";

const liBaseClasses = "group not-first-of-type:-ml-[2px] hover:z-10 focus-within:z-10";
const responsiveLiClass = "max-md:hidden";
const buttonBaseClasses =
  "relative not-group-first-of-type:rounded-l-none not-group-last-of-type:rounded-r-none";
const selectedPageButtonClasses = [
  "group-aria-[current=page]:border-active group-aria-[current=page]:bg-active",
  "group-aria-[current=page]:text-light group-aria-[current=page]:cursor-default",
  "group-aria-[current=page]:data-hovered:z-10 group-aria-[current=page]:data-focus-visible:z-10",
];

interface NcPaginatorProps {
  currentPage: number;
  totalPages: number;
  maxCurrentAdjacentPages?: number;
  zeroBasedPagination?: boolean;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const NcPaginator = ({
  currentPage,
  totalPages,
  maxCurrentAdjacentPages = 2,
  zeroBasedPagination,
  onPageChange,
  isLoading,
  isDisabled,
}: NcPaginatorProps) => {
  const { t } = useI18n();
  const firstPage = zeroBasedPagination ? 0 : 1;
  const lastPage = zeroBasedPagination ? totalPages - 1 : totalPages;
  const range = (num: number) => [...Array(num)].map((_, i) => i + firstPage);
  const FilterForMaxItems = (n: number) =>
    currentPage + maxCurrentAdjacentPages >= n && currentPage - maxCurrentAdjacentPages <= n;

  const pages = range(totalPages).filter(FilterForMaxItems);

  function getPageAriaLabel(page: number, pageLabel: number) {
    const isCurrentPage = page === currentPage;
    return isCurrentPage ? `${t("current page")}, ${pageLabel}` : `${t("go to page")} ${pageLabel}`;
  }

  return (
    <nav
      data-nc="NcPaginator"
      data-testid="pagination"
      role="navigation"
      aria-label={t("Pagination navigation")}
    >
      <ul className="isolate flex">
        {isLoading ? (
          <li className="py-2">
            <NcLoadingIndicator />
          </li>
        ) : (
          <>
            <li className={cn([liBaseClasses, responsiveLiClass])}>
              <NcButton
                className={cn(buttonBaseClasses)}
                isDisabled={currentPage <= firstPage || isDisabled}
                onPress={() => onPageChange(firstPage)}
              >
                {t("First")}
              </NcButton>
            </li>

            <li className={liBaseClasses}>
              <NcButton
                className={cn(buttonBaseClasses)}
                isDisabled={currentPage <= firstPage || isDisabled}
                onPress={() => onPageChange(currentPage - 1)}
              >
                {t("Previous")}
              </NcButton>
            </li>

            {pages.map(page => {
              const pageLabel = zeroBasedPagination ? page + 1 : page;
              const pageAriaLabel = getPageAriaLabel(page, pageLabel);
              return (
                <li
                  key={page}
                  className={cn([liBaseClasses, responsiveLiClass, "aria-[current=page]:z-10"])}
                  aria-current={page === currentPage ? "page" : undefined}
                >
                  <NcButton
                    className={cn(buttonBaseClasses, selectedPageButtonClasses)}
                    onPress={() => onPageChange(page)}
                    aria-label={pageAriaLabel}
                    isDisabled={isDisabled}
                  >
                    {pageLabel}
                  </NcButton>
                </li>
              );
            })}

            <li className={liBaseClasses}>
              <NcButton
                className={cn(buttonBaseClasses)}
                isDisabled={currentPage >= lastPage || isDisabled}
                onPress={() => onPageChange(currentPage + 1)}
              >
                {t("Next")}
              </NcButton>
            </li>

            <li className={cn([liBaseClasses, responsiveLiClass])}>
              <NcButton
                className={cn(buttonBaseClasses)}
                isDisabled={currentPage >= lastPage || isDisabled}
                onPress={() => onPageChange(lastPage)}
              >
                {t("Last")}
              </NcButton>
            </li>
          </>
        )}
      </ul>
    </nav>
  );
};

export { NcPaginator };
